import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {repeat} from 'lit/directives/repeat.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import type {Json} from 'fp-ts/lib/Json.js';
import {isPlainObject} from '@gl/lit/utils/index.js';

export const ELEMENT_TAG = 'json-render';

/**
 * Renders a JSON stringified object.
 */
@customElement(ELEMENT_TAG)
export default class JSONRender extends FlywheelComponent {
  @state()
  formattedJson = '';

  private _value = {};

  @property({type: Object})
  get value() {
    return this._value;
  }
  set value(value: object) {
    this._value = value;
    this.formattedJson = this.formatJson(this._value);
  }

  @property({type: String})
  get json() {
    return this.formattedJson;
  }
  set json(value: string) {
    try {
      this._value = JSON.parse(value);
    } catch {
      this._value = {};
    }
    this.formattedJson = this.formatJson(this._value);
  }

  private formatJson(value: unknown): string {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return 'Invalid value';
    }
  }

  private renderProperty(key: PropertyKey, value: Json): TemplateResult<1> {
    if (Array.isArray(value) && value.length === 0) {
      return this.renderProperty(key, '[]');
    }

    if (Array.isArray(value) || isPlainObject(value)) {
      return html`
        ${repeat(
          Object.entries<Json>(value),
          entry => entry.at(0),
          (entry: [string, Json]): TemplateResult<1> => {
            const [key, value] = entry;
            return html`
              <details style="padding-left:10px">
                <summary>${key}</summary>
                ${this.renderProperty(key, value)}
              </details>
            `;
          }
        )}
      `;
    }

    return html` <span>${key}: ${this.formatJson(value)}</span> `;
  }

  render() {
    return html`
      <div style="outline:2px solid grey;padding:10px">
        ${this.renderProperty('json-render', this._value)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [ELEMENT_TAG]: JSONRender;
  }
}
